/// <reference types="react-scripts" />
import { Alert, Box, CssBaseline, IconButton, Snackbar, ThemeProvider } from "@mui/material";
import { SnackbarProvider } from "notistack";
import * as React from "react";
import { Dispatch } from "react";
import * as ReactDOM from "react-dom";
import { Provider, connect } from "react-redux";
import { Redirect, Route, Router, Switch } from "react-router-dom";
import { appTheme } from "source/shared/styles";
import "ui/Js/array.js";
import "ui/Js/date.js";
import "ui/Js/fetch.js";
import "ui/Js/number.js";
import "ui/Js/string.js";
import { Base } from "ui/Scripts/source/framework/base";
import { ISignalrNotification } from "ui/Scripts/source/models/common/signalrNotification";
import { SignalRConnectionManager } from "ui/Scripts/source/models/signalR/signalRConnectionManager";
import { Translations } from "ui/Scripts/source/models/translations";
import CalendarTaskConnected from "source/containers/calendarTask/calendarTaskConnected";
import CompanyConnected from "source/containers/company/companyConnected";
import CompanyGroupConnected from "source/containers/companyGroup/companyGroupConnected";
import DocumentConnected from "source/containers/document/documentConnected";
import EventConnected from "source/containers/event/eventConnected";
import FrontConnected from "source/containers/front/frontConnected";
import InstantMessageConnected from "source/containers/instantMessage/instantMessageConnected";
import LogConnected from "source/containers/log/logConnected";
import MessageConnected from "source/containers/message/messageConnected";
import RecordConnected from "source/containers/record/recordConnected";
import TaskConnected from "source/containers/task/taskConnected";
import UserConnected from "source/containers/user/userConnected";
import UserInvitationConnected from "source/containers/user/userInvitationConnected";
import UserForgotPasswordConnected from "source/containers/user/userForgotPasswordConnected";
import UserGroupConnected from "source/containers/userGroup/userGroupConnected";
import WhistleBlowConnected from "source/containers/whistleBlow/whistleBlowConnected";
import AppReportConnected from "source/containers/appReport/appReportConnected";
import RiskConnected from "source/containers/risk/riskConnected";
import { customHistory } from "source/framework/customHistory";
import * as store from "source/framework/customStore";
import * as baseService from "source/models/services/baseService";
import { ISessionState } from "source/models/session/sessionState";
import * as storeActions from "source/models/store/storeActions";
import * as storeEffects from "source/models/store/storeEffects";
import { IAlertState, IApplicationState, IAutoLogoutState, IConfirmationState } from "source/models/store/storeTypes";
import { AppNotification } from "source/components/framework/AppNotification/AppNotification";
import { ConfirmationDialog as ConfirmationDialogNew } from "source/components/framework/Dialog/ConfirmationDialog";
import { CircularLoading } from "../framework/Loading/CircularLoading";
import { InstantMessageNotification } from "../framework/Notification/InstantMessageNotification";
import "./../../../../node_modules/bootstrap/dist/css/bootstrap-grid.min.css";
import "./../../../../node_modules/bootstrap/dist/css/bootstrap-reboot.min.css";
import "./../../../../node_modules/bootstrap/dist/css/bootstrap.min.css";
import "./../../../../node_modules/react-datetime/css/react-datetime.css";
import { PrivateRoute } from "./privateRoute";
import CloseIcon from "@mui/icons-material/Close";
import { initializeChartJs } from "source/components/framework/Chart/chartInitialize";
import moment from "moment";
import { SsoEmpty } from "./ssoEmpty";

// @ts-ignore
window.Buffer = window.Buffer || require("buffer").Buffer;

export interface IAppOwnProps {
}

export interface IAppStateProps {
    fetchCount: number,
    alert: IAlertState;
    confirmation: IConfirmationState;
    autoLogout: IAutoLogoutState;
    sessionState: ISessionState;
}

export interface IAppDispatchProps {
    clearAlerts: () => void;
    clearSuccess: () => void;
    clearInfo: () => void;
    clearConfirmation: () => void;
    clearAutoLogout: () => void;
    onLogout: () => void;
}

type AppProps = IAppOwnProps & IAppStateProps & IAppDispatchProps;

interface IAppState {
    autoLogoutPercent: number;
    signalRNotifications: ISignalrNotification[];
}

const notificationAction = (onClick: () => void) => (
    <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={onClick}
    >
        <CloseIcon fontSize="small" />
    </IconButton>
);

export class App extends React.Component<AppProps, IAppState> {
    private successTimeOut: number;
    private infoTimeOut: number;
    private errorTimeOut: number;
    private autoLogoutInterval: number;
    private autoLogoutIndex: number;
    private signalRConnectionManager: SignalRConnectionManager;

    constructor(props: AppProps) {
        super(props);
        this.state = {
            autoLogoutPercent: 100,
            signalRNotifications: []
        };
    }
    
    // #region SignalR
    removeSignalRNotification = (id: string) => {
        this.setState({
            signalRNotifications: this.state.signalRNotifications.filter(i => i.id !== id)
        });
    };

    addSignalRNotification = (item: ISignalrNotification) => {
        if (!item?.modifyUser || item.modifyUser.code === this.props.sessionState.code) return;
        const signalRNotifications = this.state.signalRNotifications.slice(0);
        signalRNotifications.unshift(item);
        this.setState({
            signalRNotifications
        });
        window.setTimeout(() => {
            this.removeSignalRNotification(item.id);
        }, appConfig.signalRNotificationDuration * 1000);
    };
    // #endregion SignalR

    componentDidMount(): void {
        initializeChartJs();
        baseService.resetAutoLogoutTimer();
        moment.updateLocale("en", {
            week: {
                dow: 1
            }
        });
        moment.locale(appConfig.culture.replace("-US", ""));
        //SignalR
        this.signalRConnectionManager = new SignalRConnectionManager();
        if (this.props.sessionState.canUseSystem) {
            this.signalRConnectionManager.openConnection(appConfig.rootUrl, { onShowNotification: this.addSignalRNotification });
        }
    }

    componentWillUnmount(): void {
        clearTimeout(this.successTimeOut);
        clearTimeout(this.infoTimeOut);
        clearTimeout(this.errorTimeOut);
        clearInterval(this.autoLogoutInterval);
        this.signalRConnectionManager.closeConnection();
    }

    componentDidUpdate(prevProps: AppProps, prevState: IAppState): void {
        //SignalR
        if (this.props.sessionState.canUseSystem) {
            this.signalRConnectionManager.openConnection(appConfig.rootUrl, { onShowNotification: this.addSignalRNotification });
        }
    }

    getShowSuccessMessage = (): boolean => {
        const props = this.props;
        const result = props.alert && !!props.alert.message;
        if (result) {
            clearTimeout(this.successTimeOut);
            this.successTimeOut = window.setTimeout(() => {
                props.clearSuccess();
            }, 2500);
            //Base.logTest("test");
        }
        return result;
    };

    getShowInfoMessage = (): boolean => {
        const props = this.props;
        const result = props.alert && !!props.alert.info;
        if (result) {
            clearTimeout(this.infoTimeOut);
            this.infoTimeOut = window.setTimeout(() => {
                props.clearInfo();
            }, 5000);
        }
        return result;
    };

    getShowErrorMessage = (): boolean => {
        const props = this.props;
        const result = props.alert && !!props.alert.error;
        if (result) {
            clearTimeout(this.errorTimeOut);
            this.errorTimeOut = window.setTimeout(() => {
                props.clearAlerts();
            }, 5000);
        }
        return result;
    };

    getShowLoadingImage = (): boolean => {
        return this.props.fetchCount > 0;
    };

    getShowAutoLgout = (): boolean => {
        const props = this.props;
        //console.log("getShowAutoLgout", props.autoLogout);
        const result = props.autoLogout && props.autoLogout.show;
        if (!result) {
            clearInterval(this.autoLogoutInterval);
            return false;
        }
        const pathname = location.pathname.toLowerCase();
        if (pathname.indexOf("/login") > -1 ||
            pathname.indexOf("/invitation") > -1 ||
            pathname.indexOf("/changepassword") > -1) {
            clearInterval(this.autoLogoutInterval);
            baseService.resetAutoLogoutTimer();
            return false;
        }
        //Set autologout timer if it is not set
        if (!this.autoLogoutInterval) {
            this.autoLogoutIndex = baseService.autoLogoutDialogDuration;
            clearInterval(this.autoLogoutInterval);
            this.autoLogoutInterval = window.setInterval(() => {
                this.setState({ autoLogoutPercent: Math.floor(100 * (this.autoLogoutIndex / baseService.autoLogoutDialogDuration)) });
                if (this.autoLogoutIndex === 0) {
                    props.clearAutoLogout();
                    props.onLogout();
                } else {
                    this.autoLogoutIndex = this.autoLogoutIndex - 1;
                }
            }, 1000);
            this.setState({ autoLogoutPercent: 100 });
        }
        return true;
    };

    handleSuccessClick = () => {
        clearTimeout(this.successTimeOut);
        this.props.clearSuccess();
    };

    handleInfoClick = () => {
        this.props.clearInfo();
    };

    handleErrorClick = () => {
        clearTimeout(this.errorTimeOut);
        this.props.clearAlerts();
    };

    handleAutoLogoutClick = () => {
        clearInterval(this.autoLogoutInterval);
        this.props.clearAutoLogout();
    };

    render() {
        const props = this.props;
        const state = this.state;
        const showSuccessMessage = this.getShowSuccessMessage();
        const showInfoMessage = this.getShowInfoMessage();
        const showErrorMessage = this.getShowErrorMessage();
        const showLoadingImage = this.getShowLoadingImage();
        const showAutoLogout = this.getShowAutoLgout();

        return (
            <div className="appContent">
                <Snackbar open={showSuccessMessage} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                    <Alert
                        severity="success"
                        sx={{ border: "1px solid", borderColor: "success.main" }}
                        action={notificationAction(this.handleSuccessClick)}
                    >
                        {props.alert.message}
                    </Alert>
                </Snackbar>
                <Snackbar open={showInfoMessage} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                    <Alert
                        severity="info"
                        sx={{ border: "1px solid", borderColor: "info.main" }}
                        action={notificationAction(this.handleInfoClick)}
                    >
                        {props.alert.info}
                    </Alert>
                </Snackbar>
                <Snackbar open={showErrorMessage} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                    <Alert
                        severity="error"
                        sx={{ border: "1px solid", borderColor: "error.main" }}
                        action={notificationAction(this.handleErrorClick)}
                    >
                        {props.alert.error}
                    </Alert>
                </Snackbar>
                {state.signalRNotifications.length > 0 && state.signalRNotifications.map((signalRNotification) =>
                    <AppNotification
                        key={signalRNotification.id}
                        item={signalRNotification}
                        onCloseClick={() => this.removeSignalRNotification(signalRNotification.id)}
                    />
                )}
                {showLoadingImage &&
                    <Box display="flex" alignItems="center" justifyContent="center" sx={{ width: "100vw", height: "100vh", position: "fixed", zIndex: 9999, pointerEvents: "none" }}>
                        <CircularLoading size="lg" />
                    </Box>
                }
                {showAutoLogout &&
                    <div className="autoLogout" onClick={this.handleAutoLogoutClick}>
                        <div className="message">{Translations.ClickToCancelAutomaticLogout}</div>
                        <div className="progress">
                            <div className="progress-bar progress-bar-danger" role="progressbar" aria-valuenow={state.autoLogoutPercent} aria-valuemin={0} aria-valuemax={100} style={{ width: state.autoLogoutPercent.toString(10) + "%" }}></div>
                        </div>
                    </div>
                }
                <Router history={customHistory}>
                    <Switch>
                        <Route path="/login">
                            <Redirect to="/Account/Login" />
                        </Route>
                        <Route exact path="/" render={() => (
                            <Redirect to="/front" />
                        )}/>
                        <Route exact path="/publicinvitation/:token" component={UserInvitationConnected}/>
                        <Route exact path="/publicpasswordchange/:token" component={UserInvitationConnected}/>
                        <Route exact path="/publicforgotpassword/:returnUrl" component={UserForgotPasswordConnected}/>
                        <Route exact path="/publicempty" component={SsoEmpty}/>
                        <PrivateRoute exact path="/front" sessionState={props.sessionState} component={FrontConnected}/>
                        <PrivateRoute exact path="/new/calendar" sessionState={props.sessionState} component={CalendarTaskConnected}/>
                        <PrivateRoute exact path="/new/company" sessionState={props.sessionState} component={CompanyConnected}/>
                        <PrivateRoute exact path="/new/companygroup" sessionState={props.sessionState} component={CompanyGroupConnected}/>
                        <PrivateRoute exact path="/new/document" sessionState={props.sessionState} component={DocumentConnected}/>
                        <PrivateRoute exact path="/new/event/:page?" sessionState={props.sessionState} component={EventConnected}/>
                        <PrivateRoute exact path="/new/instantmessage/:channelId?" sessionState={props.sessionState} component={InstantMessageConnected}/>
                        <PrivateRoute exact path="/new/log" sessionState={props.sessionState} component={LogConnected}/>
                        <PrivateRoute exact path="/new/record/:page?" sessionState={props.sessionState} component={RecordConnected}/>
                        <PrivateRoute exact path="/new/message" sessionState={props.sessionState} component={MessageConnected}/>
                        <PrivateRoute exact path="/new/task/:page?" sessionState={props.sessionState} component={TaskConnected}/>
                        <PrivateRoute exact path="/new/user" sessionState={props.sessionState} component={UserConnected}/>
                        <PrivateRoute exact path="/new/usergroup" sessionState={props.sessionState} component={UserGroupConnected}/>
                        <PrivateRoute exact path="/new/whistleblow/:page?" sessionState={props.sessionState} component={WhistleBlowConnected}/>
                        <PrivateRoute exact path="/new/report/:page?" sessionState={props.sessionState} component={AppReportConnected}/>
                        <PrivateRoute exact path="/new/risk/:page?" sessionState={props.sessionState} component={RiskConnected}/>
                        <Route>
                            <Redirect to="/" />
                        </Route>
                    </Switch>
                </Router>
                {props.confirmation.show &&
                    <ConfirmationDialogNew
                        type={props.confirmation.type}
                        title={props.confirmation.title}
                        text={!Base.isFormattedText(props.confirmation.text) ? props.confirmation.text : <div dangerouslySetInnerHTML={{ __html: Base.getFormattedText(props.confirmation.text) }}></div>}
                        yesConfirmationText={props.confirmation.yesConfirmationText}
                        onYes={props.confirmation.onYes}
                        onNo={props.confirmation.onNo}
                        onCancel={props.confirmation.onCancel}
                    />
                }
            </div>
        );
    }
}

export function mapStateToProps(state: IApplicationState): IAppStateProps {
    return {
        fetchCount: state.fetchCount,
        alert: state.alert,
        confirmation: state.confirmation,
        autoLogout: state.autoLogout,
        sessionState: state.session.state,
    };
}

export function mapDispatchToProps(dispatch: Dispatch<any>): IAppDispatchProps {
    return {
        clearAlerts: () => dispatch(storeActions.clearMessages()),
        clearSuccess: () => dispatch(storeActions.clearSuccessMessage()),
        clearInfo: () => dispatch(storeActions.clearInfoMessage()),
        clearConfirmation: () => dispatch(storeActions.clearConfirmation()),
        clearAutoLogout: () => dispatch(storeActions.autoLogoutClear()),
        onLogout: () => dispatch(storeEffects.logout()),
    };
}

export const AppConnected = connect<IAppStateProps, IAppDispatchProps, IAppOwnProps>(mapStateToProps, mapDispatchToProps)(App);

if (!window.Promise) {
    window.Promise = Promise;
}

if (!("geolocation" in navigator)) {
    console.log("[App] geolocation IS NOT available");
}

// Registering service worker
if (!("serviceWorker" in navigator)) {
    console.log("[App] serviceWorker IS NOT available");
}

const ConnectedApp = () => (
    <Provider store={store.customStore}>
        <AppConnected />
    </Provider>
);

declare module "notistack" {
    // eslint-disable-next-line no-unused-vars
    interface VariantOverrides {
        instantMessage: true;
    }
}

function startApp() {
    ReactDOM.render(
        <SnackbarProvider
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            Components={{
                instantMessage: InstantMessageNotification,
            }}
            variant="instantMessage"
            autoHideDuration={15000}
        >
            <ThemeProvider theme={appTheme}>
                <CssBaseline />
                <ConnectedApp />
            </ThemeProvider>
        </SnackbarProvider>,
        document.getElementById("appContent")
    );
};

startApp();